import React, { useState, useEffect, useContext } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  DarkMode,
  LightMode,
} from '@mui/icons-material';
import { ColorModeContext } from '../App';

const StyledAppBar = styled(AppBar)(({ theme, scrolled }) => ({
  backgroundColor: scrolled
    ? theme.palette.mode === 'dark'
      ? 'rgba(10, 15, 28, 0.85)'
      : 'rgba(255, 255, 255, 0.85)'
    : 'transparent',
  backdropFilter: scrolled ? 'blur(12px) saturate(180%)' : 'none',
  WebkitBackdropFilter: scrolled ? 'blur(12px) saturate(180%)' : 'none',
  boxShadow: 'none',
  borderBottom: scrolled ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
}));

const Logo = styled(Box)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  '& .accent': {
    color: theme.palette.primary.main,
  },
  '&:hover .accent': {
    opacity: 0.7,
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: 'none',
  padding: '6px 12px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  },
  '& .num': {
    color: theme.palette.primary.main,
    marginRight: '4px',
  },
}));

const ThemeToggle = styled(IconButton)(({ theme }) => ({
  marginLeft: '8px',
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  width: 36,
  height: 36,
  '&:hover': {
    color: theme.palette.text.primary,
    borderColor: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
}));

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const colorMode = useContext(ColorModeContext);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'about', href: '#about' },
    { label: 'experience', href: '#experience' },
    { label: 'projects', href: '#projects' },
    { label: 'skills', href: '#skills' },
    { label: 'contact', href: '#contact' },
  ];

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const drawer = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 2 }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item, i) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              onClick={() => handleNavClick(item.href)}
              sx={{ py: 2, px: 3 }}
            >
              <ListItemText
                primary={
                  <Box
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.95rem',
                    }}
                  >
                    <Box component="span" sx={{ color: 'primary.main', mr: 1 }}>
                      0{i + 1}.
                    </Box>
                    {item.label}
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <StyledAppBar position="fixed" scrolled={scrolled ? 1 : 0}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1, minHeight: { xs: 56, md: 64 } }}>
            <Logo onClick={() => handleNavClick('#home')}>
              <span className="accent">~/</span>david.owuor
            </Logo>

            <Box sx={{ flexGrow: 1 }} />

            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {navItems.map((item, i) => (
                  <NavButton
                    key={item.label}
                    onClick={() => handleNavClick(item.href)}
                  >
                    <span className="num">0{i + 1}.</span>
                    {item.label}
                  </NavButton>
                ))}
              </Box>
            )}

            <ThemeToggle onClick={colorMode.toggleColorMode} aria-label="toggle theme">
              {theme.palette.mode === 'dark' ? (
                <LightMode sx={{ fontSize: '1.125rem' }} />
              ) : (
                <DarkMode sx={{ fontSize: '1.125rem' }} />
              )}
            </ThemeToggle>

            {isMobile && (
              <IconButton
                onClick={handleDrawerToggle}
                sx={{ ml: 1 }}
                aria-label="open menu"
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </StyledAppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: { backgroundColor: theme.palette.background.paper },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
